import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_LAYOUT } from '@progress/kendo-angular-layout';
import { AhfcIdentityErrorCodes, ApiException, AuthSessionDto, isUnityIdentityProblem, UnitySessionGrantTypes } from '@unity/clients';
import { OutsideContainerComponent } from '@unity/components/layouts/outside-container/outside-container.component';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { AuthService } from '@unity/services/auth.service';
import { NavigationService } from '@unity/services/navigation.service';
import { RouteService } from '@unity/services/route.service';
import { SessionService } from '@unity/services/session.service';
import { ToasterService } from '@unity/services/toaster.service';
import { stringFormControl } from '@unity/utils/form-controls';
import { FormGroupControlSchema } from '@unity/utils/types';
import { filter, map, mergeMap, Observer, switchMap, take } from 'rxjs';

class LoginSchema implements FormGroupControlSchema<{ username: string; password: string }> {
  public username = stringFormControl();
  public password = stringFormControl();
}

@Component({
  selector: 'unity-login-page',
  standalone: true,
  imports: [KENDO_BUTTON, KENDO_LOADER, KENDO_LAYOUT, ReactiveFormsModule, RouterLink, OutsideContainerComponent, TextboxControlComponent],
  styleUrl: './login-page.component.scss',
  templateUrl: './login-page.component.html',
})
export class LoginPageComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly sessionService = inject(SessionService);
  private readonly toasterService = inject(ToasterService);
  private readonly navigationService = inject(NavigationService);
  private readonly routeService = inject(RouteService);

  protected loginForm = this.formBuilder.group<LoginSchema>(new LoginSchema());

  protected readonly $isLoading = signal<boolean>(false);
  protected readonly $isUnconfirmed = signal<boolean>(false);

  private sessionObserver: Observer<AuthSessionDto> = {
    error: (e: Error) => {
      if (
        ApiException.isApiException(e) &&
        isUnityIdentityProblem(e.result) &&
        e.result.error_code == AhfcIdentityErrorCodes.EmailNotConfirmed
      ) {
        this.$isUnconfirmed.set(true);
        this.$isLoading.set(false);
        return;
      }
      this.toasterService.showError('Unknown error', e, 'error');
      this.$isLoading.set(false);
      // do error here
    },
    next: res => this.sessionService.handleSessionResponse(res),
    complete: () => this.$isLoading.set(false),
  };

  private selectAgencyIfAlreadyLoggedIn() {
    const loggedIn = this.sessionService.$isLoggedIn();
    const expired = this.sessionService.$isExpired();
    if (loggedIn && !expired) {
      this.navigationService.navigate(['/login-select-business-partner']);
    }
  }

  private handleMfaIfProvided() {
    this.routeService
      .getActiveRoute()
      .queryParams.pipe(
        map(params => ({
          success: params['mfa_success'],
          username: params['username'],
          code: params['code'],
          state: params['state'],
        })),
        filter(params => params.success && params.success === 'true' && params.username && params.code && params.state),
        switchMap(params => {
          const sessionResponse = this.authService.createAcgSession({
            username: params.username,
            mfa_code: params.code,
            mfa_state: params.state,
          });
          return sessionResponse;
        })
      )
      .subscribe(this.sessionObserver);
  }

  public ngOnInit() {
    this.selectAgencyIfAlreadyLoggedIn();
    this.handleMfaIfProvided();
  }

  protected onSubmit() {
    this.$isLoading.set(true);
    this.$isUnconfirmed.set(false);
    const username = this.loginForm.controls.username.value;
    const password = this.loginForm.controls.password.value;
    this.sessionService.clean();

    this.authService
      .getAuthFlow(username)
      .pipe(
        take(1),
        mergeMap(flowResponse => {
          const contentType = flowResponse.grant_type;
          if (contentType == UnitySessionGrantTypes.ACGv1) {
            return this.authService.startMfaFlow(username, password);
          } else if (contentType == UnitySessionGrantTypes.ROPGv1) {
            return this.authService.createRopgSession({ username, password });
          } else {
            throw new Error(`${contentType} is not a recognized authentication flow content-type`);
          }
        })
      )
      .subscribe(this.sessionObserver);
  }

  protected reset() {
    this.$isLoading.set(false);
    this.$isUnconfirmed.set(false);
    this.loginForm.reset();
  }
}
